import {
	BackendService,
	GameConfigService,
	IVehicleService,
	RpcService,
} from '~/rage';
import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import {
	ITechTuningItem,
	TechTuningCases,
	TechTuningCaseType,
	WalletType,
} from '~/shared';
import {
	ITechTuningService,
	TechTuningCaseConfigByType,
	VehicleTechTuningItemConfig,
} from './techTuningServiceTypes';

export class TechTuningService implements ITechTuningService {
	private readonly items = new Subject<VehicleTechTuningItemConfig[]>([]);
	private readonly shopItems = new Subject<VehicleTechTuningItemConfig[]>([]);
	private readonly caseItems: Record<
		TechTuningCaseType,
		Subject<VehicleTechTuningItemConfig[]>
	> = Object.values(TechTuningCaseType).reduce(
		(acc, type) => {
			acc[type] = new Subject<VehicleTechTuningItemConfig[]>([]);
			return acc;
		},
		{} as Record<TechTuningCaseType, Subject<VehicleTechTuningItemConfig[]>>,
	);

	private readonly ownedItems = new Subject<ITechTuningItem[]>([]);
	private readonly installedItems = new Subject<string[]>([]);
	private cases = new Subject<TechTuningCaseConfigByType | null>(null);

	constructor(
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
		private readonly rpc: RpcService,
		private readonly vehicles: IVehicleService,
	) {
		this.gameConfig.getConfigs().subscribe(() => {
			this.updateItems();
			this.updateCases();
		});

		this.vehicles.getLocalVehicleModel().subscribe(() => this.updateItems());

		this.backend.addEventHandler(
			'techTuning:owned:update',
			(data: { items: ITechTuningItem[] }) => {
				this.ownedItems.next(data.items);
			},
		);

		this.backend.addEventHandler(
			'techTuning:cases:update',
			this.handleCasesUpdate.bind(this),
		);
	}

	private handleCasesUpdate(data: { cases: TechTuningCases }) {
		const cases = this.cases.getValue();
		if (!cases) {
			return;
		}

		const casesNew = { ...cases };

		for (const [caseType, caseCount] of Object.entries(data.cases)) {
			const typedCaseType = caseType as TechTuningCaseType;
			const caseConfig = casesNew[typedCaseType];
			if (!caseConfig) {
				continue;
			}

			casesNew[typedCaseType] = {
				...caseConfig,
				inStock: caseCount,
			};
		}

		this.cases.next(casesNew);
	}

	public async fetchInstalledItems(): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		const installedItems = await this.backend.invoke<string[]>(
			'techTuning:getInstalledItems',
			{ vehicleId },
		);
		this.installedItems.next(installedItems);
	}

	private updateItems() {
		if (!this.gameConfig.configs) {
			return;
		}

		const model = this.vehicles.getLocalVehicleModel().getValue();
		const configItems =
			this.gameConfig.configs.vehicleTechTuning?.items?.filter(
				(item) => item.model === model,
			) ?? [];
		this.items.next(configItems);

		this.shopItems.next(configItems.filter((item) => item.isShopItem));

		const configCases = this.gameConfig.configs.vehicleTechTuning?.cases ?? [];
		for (const caseConfig of configCases) {
			const caseSubj = this.caseItems[caseConfig.type];
			const caseItems = configItems.filter((item) =>
				caseConfig.itemsRarity.includes(item.rarity),
			);
			caseSubj.next(caseItems);
		}
	}

	private updateCases() {
		if (!this.gameConfig.configs) {
			return;
		}

		const configCases = this.gameConfig.configs.vehicleTechTuning?.cases ?? [];
		const ownedCases: Partial<TechTuningCaseConfigByType> = {};

		for (const caseType of Object.values(TechTuningCaseType)) {
			const caseConfig = configCases.find((e) => e.type === caseType);
			if (!caseConfig) continue;

			ownedCases[caseType] = {
				...caseConfig,
				inStock: 0,
			};
		}

		this.cases.next(ownedCases as TechTuningCaseConfigByType);
	}

	public getItems(): IObservable<VehicleTechTuningItemConfig[]> {
		return this.items;
	}

	public getShopItems(): IObservable<VehicleTechTuningItemConfig[]> {
		return this.shopItems;
	}

	public getCaseItems(
		caseType: TechTuningCaseType,
	): IObservable<VehicleTechTuningItemConfig[]> {
		return this.caseItems[caseType];
	}

	public getOwnedItems(): IObservable<ITechTuningItem[]> {
		return this.ownedItems;
	}

	public getInstalledItems(): IObservable<string[]> {
		return this.installedItems;
	}

	private getItemById(itemId: string): VehicleTechTuningItemConfig | null {
		const vehicleModel = this.vehicles.getLocalVehicleModel().getValue();
		if (!vehicleModel || !this.gameConfig.configs) {
			return null;
		}

		const itemConfigs =
			this.gameConfig.configs.vehicleTechTuning?.modelsToItems?.[
				vehicleModel
			] ?? [];
		return itemConfigs.find((e) => e.id === itemId) ?? null;
	}

	public getItemBuyPrice(itemId: string): number {
		const itemConfig = this.getItemById(itemId);
		if (!itemConfig) {
			return 0;
		}

		const discount = itemConfig.discount ?? 0;
		return Math.floor(itemConfig.price * (1 - discount));
	}

	public getItemSellPrice(itemId: string): number {
		if (!this.gameConfig.configs) {
			return 0;
		}

		const sellPriceRate =
			this.gameConfig.configs.vehicleTechTuning?.sellPriceRate ?? 0.5;
		const itemPrice = this.getItemBuyPrice(itemId);
		return Math.floor(itemPrice * sellPriceRate);
	}

	@notifyOnError
	public async buyItem(itemId: string, walletType: WalletType): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		await this.backend.invoke('techTuning:buy', {
			vehicleId,
			itemId,
			walletType,
		});
		await this.fetchInstalledItems();
	}

	@notifyOnError
	public async installItem(itemId: string): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		await this.backend.invoke('techTuning:install', { vehicleId, itemId });
		await this.fetchInstalledItems();
	}

	@notifyOnError
	public async sellOwned(itemId: string): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		await this.backend.invoke('techTuning:sellOwned', {
			itemId,
			vehicleId,
		});
	}

	@notifyOnError
	public async sellEquipped(itemId: string): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (!vehicleId) {
			return;
		}

		await this.backend.invoke('techTuning:sellEquipped', {
			vehicleId,
			itemId,
		});
		await this.fetchInstalledItems();
	}

	public getCases(): IObservable<TechTuningCaseConfigByType | null> {
		return this.cases;
	}

	@notifyOnError
	public async buyCase(
		caseType: TechTuningCaseType,
		count: number,
	): Promise<void> {
		return await this.backend.invoke('techTuning:buyCase', { caseType, count });
	}

	@notifyOnError
	public async openCase(
		caseType: TechTuningCaseType,
	): Promise<{ itemId: string }> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		const itemId = await this.backend.invoke<string>('techTuning:openCase', {
			caseType,
			vehicleId,
		});
		return { itemId };
	}

	public async exitMenu(): Promise<void> {
		return await this.rpc.invokeClient('techTuning:exitMenu');
	}
}
